import { useRef, useState } from "react";

const PCM_CONTENT_TYPE = "audio/L16;rate=16000;channels=1";

function pcm16le(chunks: Float32Array[]) {
	const samples = chunks.reduce((count, chunk) => count + chunk.length, 0);
	const output = new ArrayBuffer(samples * 2);
	const view = new DataView(output);
	let offset = 0;
	for (const chunk of chunks) {
		for (const sample of chunk) {
			const value = Math.max(-1, Math.min(1, sample));
			view.setInt16(offset, value < 0 ? value * 0x8000 : value * 0x7fff, true);
			offset += 2;
		}
	}
	return output;
}

export function VoiceComposer({
	targetType,
	targetId,
	conversationId,
	onTurnCommitted,
	available = true,
	disabled = false,
}: {
	targetType: "projects" | "proposals";
	targetId: string;
	conversationId: string;
	onTurnCommitted: () => void | Promise<void>;
	available?: boolean;
	disabled?: boolean;
}) {
	const [supported] = useState(() => !!navigator.mediaDevices?.getUserMedia && !!window.AudioContext);
	const [status, setStatus] = useState("");
	const [recording, setRecording] = useState(false);
	const [sending, setSending] = useState(false);
	const capture = useRef<{
		context: AudioContext;
		processor: ScriptProcessorNode;
		stream: MediaStream;
		chunks: Float32Array[];
	} | null>(null);
	if (!available) return <p role="status">Voice input is not configured. Text messaging remains available.</p>;
	if (!supported)
		return <p role="status">Voice input is unavailable in this browser. Text messaging remains available.</p>;

	async function start() {
		setStatus("");
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, sampleRate: 16000 } });
			const context = new AudioContext({ sampleRate: 16000 });
			const source = context.createMediaStreamSource(stream);
			const processor = context.createScriptProcessor(4096, 1, 1);
			const chunks: Float32Array[] = [];
			processor.onaudioprocess = (event) => chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
			source.connect(processor);
			processor.connect(context.destination);
			capture.current = { context, processor, stream, chunks };
			setRecording(true);
			setStatus("Recording…");
		} catch {
			setStatus("Microphone unavailable. Use text instead.");
		}
	}

	async function stop() {
		const current = capture.current;
		if (!current) return;
		capture.current = null;
		current.processor.disconnect();
		current.stream.getTracks().forEach((track) => {
			track.stop();
		});
		await current.context.close();
		setRecording(false);
		setStatus("Transcribing…");
		setSending(true);
		let accepted = false;
		try {
			const response = await fetch(
				`/api/portal/v1/${targetType}/${targetId}/conversations/${conversationId}/voice`,
				{
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": PCM_CONTENT_TYPE, "Idempotency-Key": crypto.randomUUID() },
					body: pcm16le(current.chunks),
				},
			);
			if (!response.ok) throw new Error("voice upload failed");
			await response.text();
			accepted = true;
		} catch {
			setStatus("Voice message failed. Use text instead.");
		} finally {
			setSending(false);
		}
		if (accepted) {
			try {
				await onTurnCommitted();
			} catch {
				// Conversation polling owns recovery after the server accepts the voice turn.
			} finally {
				setStatus("");
			}
		}
	}

	const state = recording ? "recording" : sending ? "transcribing" : "idle";
	const label =
		state === "recording"
			? "Stop and send voice recording"
			: state === "transcribing"
				? "Transcribing voice message"
				: "Start voice recording";
	return (
		<div className="voice-composer">
			<button
				className="secondary voice-toggle"
				type="button"
				onClick={recording ? stop : start}
				aria-label={label}
				aria-pressed={recording}
				aria-describedby="voice-status"
				title={label}
				data-state={state}
				disabled={sending || disabled}
			>
				<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
					<path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z" />
					<path d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3M9 22h6" />
				</svg>
				<span className="voice-toggle-indicator" aria-hidden="true" />
			</button>
			<span id="voice-status" className="voice-status" role="status" aria-live="polite">
				{status}
			</span>
		</div>
	);
}
